import { useEffect, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { useMerchants } from '../../merchants/hooks/useMerchants';
import { previousMonthPeriod } from '../period';

/**
 * Manual generation for a merchant/period.
 *
 * The backend 409s when a report for that merchant/period already exists; the
 * page catches that and flips this modal into a confirm-regenerate state rather
 * than surfacing a raw conflict. Regenerating is destructive: the service
 * DELETES the existing report and its versions before creating the new one, so
 * any prior TCS edits and the earlier source snapshot are lost. That warning is
 * spelled out rather than implied.
 */
export function GenerateReportModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  conflict,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: { merchantId: string; period: string; force: boolean }) => void;
  isSubmitting: boolean;
  /** Set by the page after a 409, to switch the primary action to a forced regenerate. */
  conflict: string | null;
}) {
  const { data: merchants = [], isLoading: merchantsLoading } = useMerchants({ enabled: isOpen });
  const [merchantId, setMerchantId] = useState('');
  const [period, setPeriod] = useState(previousMonthPeriod());

  useEffect(() => {
    if (isOpen) {
      setMerchantId('');
      setPeriod(previousMonthPeriod());
    }
  }, [isOpen]);

  const canSubmit = !!merchantId && /^\d{4}-\d{2}$/.test(period) && !isSubmitting;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-6">
        <h2 className="text-[18px] font-bold text-foreground">Generate a report</h2>
        <p className="text-[13px] text-muted-foreground mt-1">
          Aggregates the merchant&apos;s analytics for the period and drafts a narrative. The result
          lands in <span className="font-medium text-foreground">Pending review</span> and is never
          visible to the merchant until it is approved and released.
        </p>

        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="text-[13px] font-medium text-foreground">Merchant</span>
            {/*
              A native <select> rather than the shared <Select>: that component
              requires a DropdownProvider, and Osprey has no global one yet
              (PE-2216 adds it). Adding another local provider here would deepen
              exactly the duplication that ticket removes, so this uses a plain
              control styled with the same tokens until the global provider lands.
            */}
            <select
              value={merchantId}
              onChange={(e) => setMerchantId(e.target.value)}
              disabled={merchantsLoading}
              className="mt-1 h-[40px] w-full rounded-[8px] border border-border bg-background px-3 text-sm text-foreground disabled:opacity-50"
            >
              <option value="">{merchantsLoading ? 'Loading merchants…' : 'Select a merchant…'}</option>
              {merchants.map((m: { id: string; name: string; slug?: string }) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.slug ? ` (${m.slug})` : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-[13px] font-medium text-foreground">Period</span>
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="mt-1 h-[40px] w-full rounded-[8px] border border-border bg-background px-3 text-sm text-foreground"
            />
            <span className="mt-1 block text-[12px] text-muted-foreground">
              Defaults to last month, matching the scheduled monthly run.
            </span>
          </label>

          {conflict && (
            <div className="rounded-[10px] border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
              <p className="font-medium">A report already exists for this merchant and period.</p>
              <p className="mt-1">
                Regenerating deletes the existing report, including any edits made during review and
                its recorded source snapshot. This cannot be undone.
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" size="sm" className="h-[40px]" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-[40px]"
            disabled={!canSubmit}
            loading={isSubmitting}
            onClick={() => onSubmit({ merchantId, period, force: !!conflict })}
          >
            {conflict ? 'Delete and regenerate' : 'Generate'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
